import { RootState } from "@/redux/store";
import { useMJEDownloadTemplate } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { toast } from "@/components/Toast";

const useDownloadTemplate = ({configFields, currentConfig}: {configFields: any, currentConfig: any}) => {
  const loggedInUserData = useSelector((state: RootState) => state.currentData.loggedInUserData);
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const queryClient = useQueryClient();
  const documentTypeId = currentConfig?.documentTypeId;

  const headerFields = useMemo(() => configFields?.filter(
    (f: any) => f?.fieldLevel?.toLowerCase() == "header" && f.applicable
  ) || [], [configFields]);

  const lineItemFields = useMemo(() => configFields?.filter(
    (f: any) => f?.fieldLevel?.toLowerCase() == "line_item" && f.applicable
  ) || [], [configFields]);

  const templateMetadata = useMemo(() => {
    return [
      {label: 'Doc Type', value: currentConfig?.documentTypeDisplayName || ""},
      {label: 'Prefix', value: currentConfig?.documentType || ""},
      {label: 'Total Header Fields', value: headerFields?.length || "0"},
      {label: 'Total Line Items', value: lineItemFields?.length || "0"},
      {label: 'Generated On', value: moment().format('MM/DD/YYYY')},
      {label: 'Generated By', value: loggedInUserData?.fullName},
    ]
  }, [currentConfig]);

  const {
    data: reportExportData,
    refetch: refetchReportExportData,
    isRefetching: isReportExportLoading,
  } = useMJEDownloadTemplate({
    documentTypeId: documentTypeId as string,
    communityId: communityData?.communityId as string,
    queryParams: {
      enabled: false,
    },
  });

  useEffect(() => {
    if (!reportExportData) return;

    try {
      const blob = new Blob([reportExportData.data], {
        type: reportExportData.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = reportExportData.headers["content-disposition"];
      let downloadFileName = `${communityData?.communityId}-MJE-Template.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^\"]+)"?/);
        if (match?.[1]) downloadFileName = match[1];
      }

      link.setAttribute("download", downloadFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "There is an issue while downloading the file"
      );
    } finally {
      queryClient.removeQueries({
        queryKey: ["mje-download-template", communityData?.communityId, documentTypeId],
      });
    }
  }, [reportExportData]);

  const onDownload = () => {
    refetchReportExportData();
  }

  return {
    headerFields,
    lineItemFields,
    templateMetadata,
    onDownload,
    isReportExportLoading
  }
}

export default useDownloadTemplate;